import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import NotificationBell from '../notifications/NotificationBell';
import {
    Menu,
    X,
    Home,
    MapPin,
    Car,
    User,
    LogOut,
    Settings,
    ChevronDown,
    Shield,
    AlertTriangle,
    HandPlatter,
    Truck,
    Briefcase
} from 'lucide-react';

const TopbarNav: React.FC = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

    const handleLogout = async () => {
        console.log('🔴 LOGOUT: handleLogout function called!');
        console.log('🔴 LOGOUT: Current auth state - isAuthenticated:', isAuthenticated, 'user:', user);

        try {
            console.log('🔴 LOGOUT: About to call logout()');
            await logout();
            console.log('🔴 LOGOUT: logout() completed');

            setIsUserMenuOpen(false);
            setIsMobileMenuOpen(false);
            navigate('/');
        } catch (error) {
            console.error('🔴 LOGOUT: Failed:', error);
            // Still close menus and navigate even if logout fails
            setIsUserMenuOpen(false);
            setIsMobileMenuOpen(false);
            navigate('/');
        }
    };

    const handleLogin = () => {
        setIsMobileMenuOpen(false);
        navigate('/login');
    };

    const handleRegister = () => {
        setIsMobileMenuOpen(false);
        navigate('/register');
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const isActiveRoute = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const isAdmin = user?.roles?.some((role: any) => role.name === 'admin') || false;
    const isDriver = user?.roles?.some((role: any) => role.name === 'driver') || false;
    const isServiceProvider = user?.roles?.some((role: any) => role.name === 'service_provider') || false;
    
    const navLinks = [
        { path: '/', label: 'Home', icon: Home, requiresAuth: false },
        { path: '/routes', label: 'Routes', icon: MapPin, requiresAuth: true },
        { path: '/trips', label: 'Trips', icon: Car, requiresAuth: true },
        { path: '/vehicles', label: 'Vehicles', icon: Truck, requiresAuth: true, requiresDriver: true },
        { path: '/services', label: 'Services', icon: HandPlatter, requiresAuth: true },
        { path: '/careers', label: 'Careers', icon: Briefcase, requiresAuth: false },
    ];
    // Only log auth state changes, not every render
    React.useEffect(() => {
        console.log('🔵 AUTH STATE CHANGE: isAuthenticated:', isAuthenticated, 'user:', user);
    }, [isAuthenticated, user]);

    return (
        <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo and App Name */}
                    <div className="flex items-center">
                        <Link to="/" className="flex items-center space-x-2">
                            <div className="flex items-center justify-center w-20 h-20 rounded-lg">
                                <img src="/logo.png" alt="DandiaLift Logo" className="h-19 w-19 object-contain" />
                            </div>
                            <span className="text-xl font-bold text-gray-900 hidden sm:block">
                                DandiaLift
                            </span>
                            <span className="text-xl font-bold text-gray-900 sm:hidden">
                                DandiaLift
                            </span>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {navLinks.map(({ path, label, icon: Icon, requiresAuth, requiresDriver }) => {
                            const shouldShow = (!requiresAuth || (requiresAuth && isAuthenticated)) &&
                                             (!requiresDriver || (requiresDriver && isDriver));
                            
                            return shouldShow && (
                                <Link
                                    key={path}
                                    to={path}
                                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActiveRoute(path)
                                        ? 'text-blue-600 bg-blue-50'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span>{label}</span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Desktop User Menu */}
                    <div className="hidden md:flex items-center space-x-4">
                        {isAuthenticated && user ? (
                            <>
                            <NotificationBell />
                            <div className="relative">
                                <button
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center justify-center w-8 h-8 bg-gray-200 text-gray-600 rounded-full overflow-hidden">
                                        {user.profile_picture_url ? (
                                            <img
                                                src={user.profile_picture_url}
                                                alt={`${user.first_name}'s profile`}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <User className="h-4 w-4" />
                                        )}
                                    </div>
                                    <span className="max-w-32 truncate">{user.first_name}</span>
                                    {!user.phone_number && (
                                        <span title="Add your phone number to complete your profile" aria-label="Profile incomplete: phone number missing">
                                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                                        </span>
                                    )}
                                    <ChevronDown className="h-4 w-4" />
                                </button>

                                {/* User Dropdown Menu */}
                                {isUserMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                                        <Link
                                            to="/profile"
                                            onClick={() => setIsUserMenuOpen(false)}
                                            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <Settings className="h-4 w-4" />
                                            <span>Profile Settings</span>
                                            {!user.phone_number && <AlertTriangle className="h-4 w-4 text-amber-500 ml-auto" />}
                                        </Link>
                                        {!isDriver && (
                                            <Link
                                                to="/apply-driver"
                                                onClick={() => setIsUserMenuOpen(false)}
                                                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                                <User className="h-4 w-4" />
                                                <span>Become a Driver</span>
                                            </Link>
                                        )}
                                        {isAdmin && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setIsUserMenuOpen(false)}
                                                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                                <Shield className="h-4 w-4" />
                                                <span>Admin Panel</span>
                                            </Link>
                                        )}
                                        <hr className="my-1" />
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log('🟡 BUTTON: Click event fired');
                                                handleLogout();
                                            }}
                                            className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            <span>Sign out</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            </>
                        ) : (
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handleLogin}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    Sign in
                                </button>
                                <button
                                    onClick={handleRegister}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Sign up
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                        >
                            {isMobileMenuOpen ? (
                                <X className="h-6 w-6" />
                            ) : (
                                <Menu className="h-6 w-6" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-gray-200 relative z-50">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            {navLinks.map(({ path, label, icon: Icon, requiresAuth, requiresDriver }) => {
                                const shouldShow = (!requiresAuth || (requiresAuth && isAuthenticated)) &&
                                                 (!requiresDriver || (requiresDriver && isDriver));
                                
                                return shouldShow && (
                                    <Link
                                        key={path}
                                        to={path}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            console.log('🟢 MOBILE NAV: Clicked', label, 'to', path);
                                            closeMobileMenu();
                                        }}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors ${isActiveRoute(path)
                                            ? 'text-blue-600 bg-blue-50'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                            }`}
                                    >
                                        <Icon className="h-5 w-5" />
                                        <span>{label}</span>
                                    </Link>
                                );
                            })}

                            {/* Mobile User Section */}
                            <div className="border-t border-gray-200 pt-3 mt-3">
                                {isAuthenticated && user ? (
                                    <div className="space-y-1">
                                        <div className="flex items-center space-x-2 px-3 py-2">
                                            <div className="flex items-center justify-center w-8 h-8 bg-gray-200 text-gray-600 rounded-full overflow-hidden">
                                                {user.profile_picture_url ? (
                                                    <img
                                                        src={user.profile_picture_url}
                                                        alt={`${user.first_name}'s profile`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <User className="h-4 w-4" />
                                                )}
                                            </div>
                                            <span className="text-base font-medium text-gray-900">
                                                {user.first_name}
                                            </span>
                                        </div>
                                        <Link
                                            to="/profile"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log('🟢 MOBILE NAV: Clicked Profile');
                                                closeMobileMenu();
                                            }}
                                            className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                        >
                                            <Settings className="h-5 w-5" />
                                            <span>Profile Settings</span>
                                        </Link>
                                        <Link
                                            to="/apply-driver"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log('🟢 MOBILE NAV: Clicked Apply Driver');
                                                closeMobileMenu();
                                            }}
                                            className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                        >
                                            <User className="h-5 w-5" />
                                            <span>Become a Driver</span>
                                        </Link>
                                        {isAdmin && (
                                            <Link
                                                to="/admin"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('🟢 MOBILE NAV: Clicked Admin Panel');
                                                    closeMobileMenu();
                                                }}
                                                className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                            >
                                                <Shield className="h-5 w-5" />
                                                <span>Admin Panel</span>
                                            </Link>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log('🟡 MOBILE BUTTON: Click event fired');
                                                handleLogout();
                                            }}
                                            className="flex items-center space-x-2 w-full px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        >
                                            <LogOut className="h-5 w-5" />
                                            <span>Sign out</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        <button
                                            onClick={handleLogin}
                                            className="flex items-center justify-center w-full px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                        >
                                            Sign in
                                        </button>
                                        <button
                                            onClick={handleRegister}
                                            className="flex items-center justify-center w-full px-3 py-2 text-base font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Sign up
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Overlay for mobile menu */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-25 z-30 md:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Overlay for user dropdown */}
            {isUserMenuOpen && (
                <div
                    className="fixed inset-0 z-30 hidden md:block"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsUserMenuOpen(false);
                    }}
                />
            )}
        </nav>
    );
};

export default TopbarNav;
